import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2, XCircle, AlertCircle, RefreshCw, Upload, 
  Package, Image, DollarSign, Eye, ChevronDown, ChevronRight,
  Search, BarChart3
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import apiClient from '../../lib/api';

const STORE_ID = '52f68b0c-bb0b-41ee-9529-f6dda58e6d04';
const CATALOG_ID = '66f51daf-b560-416a-b0c3-d928206efef5';

const StatusBadge = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
    ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
  }`} data-testid={`badge-${label.toLowerCase().replace(/\s/g, '-')}`}>
    {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
    {label}
  </span>
);

const IFoodCatalogV2Tab = () => {
  const [checklist, setChecklist] = useState(null);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedCat, setExpandedCat] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [checkRes, itemsRes] = await Promise.all([
        apiClient.get(`/ifood/v2/${STORE_ID}/checklist`),
        apiClient.get(`/ifood/v2/${STORE_ID}/items`),
      ]);
      setChecklist(checkRes.data);
      setItems(itemsRes.data.items || []);
    } catch (err) {
      toast.error('Erro ao carregar dados V2');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = async () => {
    try {
      // Sempre buscar o catalog_id atualizado do checklist (em vez de hardcoded)
      const realCatalogId = checklist?.catalog_id || CATALOG_ID;
      const res = await apiClient.get(`/ifood/v2/${STORE_ID}/catalogs/${realCatalogId}/categories`);
      setCategories(res.data);
      if (res.data?.total_categories === 0) {
        toast.info('Nenhuma categoria retornada pelo iFood para este catalog_id.');
      } else {
        toast.success(`${res.data?.total_categories || 0} categorias carregadas`);
      }
    } catch (err) {
      toast.error('Erro ao buscar categorias do iFood: ' + (err?.response?.data?.detail || err.message || ''));
    }
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleUploadAll = async () => {
    setUploading(true);
    setUploadProgress('Iniciando upload massivo...');
    try {
      const res = await apiClient.post(`/ifood/v2/${STORE_ID}/image/upload-all`);
      const data = res.data;
      setUploadProgress(null);
      toast.success(`Upload concluido: ${data.uploaded} enviadas, ${data.skipped} ja existentes, ${data.failed} falhas`);
      fetchAll();
    } catch (err) {
      toast.error('Erro no upload massivo');
      setUploadProgress(null);
    } finally {
      setUploading(false);
    }
  };

  const handleUploadSingle = async (productId) => {
    try {
      const res = await apiClient.post(`/ifood/v2/${STORE_ID}/image/upload`, { product_id: productId });
      if (res.data.success) {
        toast.success(`Imagem enviada: ${res.data.product_name}`);
        fetchAll();
      } else {
        toast.error(res.data.error || 'Falha no upload');
      }
    } catch (err) {
      toast.error('Erro ao enviar imagem');
    }
  };

  const handleValidate = async () => {
    try {
      const res = await apiClient.get(`/ifood/v2/${STORE_ID}/validate`);
      toast.success(`Validacao: ${res.data.valid}/${res.data.total} (${res.data.validation_rate})`);
      fetchAll();
    } catch (err) {
      toast.error('Erro na validacao');
    }
  };

  const filteredItems = items.filter(i =>
    i.name.toLowerCase().includes(search.toLowerCase())
  );

  const syncedCount = items.filter(i => ['SYNCED', 'SYNCED_EXISTING'].includes(i.sync_status)).length;
  const withImage = items.filter(i => i.image_path).length;
  const failedCount = items.filter(i => i.sync_status === 'FAILED').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="catalog-v2-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
        <span className="ml-3 text-gray-500">Carregando Catalogo V2...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="catalog-v2-tab">
      {/* Status Header */}
      <div className={`rounded-xl p-5 border-2 ${
        checklist?.status === 'PRONTO PARA HOMOLOGACAO'
          ? 'bg-green-50 border-green-300' : 'bg-yellow-50 border-yellow-300'
      }`} data-testid="catalog-v2-status-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {checklist?.status === 'PRONTO PARA HOMOLOGACAO'
              ? <CheckCircle2 className="w-8 h-8 text-green-600" />
              : <AlertCircle className="w-8 h-8 text-yellow-600" />}
            <div>
              <h2 className="text-lg font-bold text-gray-900" data-testid="catalog-v2-status-text">
                {checklist?.status || 'Carregando...'}
              </h2>
              <p className="text-sm text-gray-600">
                Merchant: {checklist?.merchant_id?.substring(0, 12)}... | Catalog: {checklist?.catalog_id?.substring(0, 12)}...
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAll} data-testid="btn-refresh-v2">
            <RefreshCw className="w-4 h-4 mr-1" /> Atualizar
          </Button>
        </div>

        {/* Lista de Pendencias detalhada */}
        {checklist?.checks?.descriptions_valid?.invalid_items?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-yellow-300" data-testid="catalog-v2-pendencias">
            <div className="text-sm font-semibold text-yellow-900 mb-2">
              {checklist.checks.descriptions_valid.invalid_count} descrição(ões) inválida(s) — corrija na tela de produto:
            </div>
            <ul className="space-y-1.5">
              {checklist.checks.descriptions_valid.invalid_items.map((it) => {
                const reasonLabel = {
                  sem_descricao: 'sem descrição',
                  descricao_curta: 'descrição muito curta (< 10 chars)',
                  descricao_igual_nome: 'descrição igual ao nome',
                }[it.reason] || it.reason;
                return (
                  <li key={it.product_id} className="text-sm text-yellow-900 flex items-start gap-2" data-testid={`pendencia-${it.product_id}`}>
                    <span className="font-medium">• {it.name}</span>
                    <span className="text-yellow-700">— {reasonLabel}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {/* Metricas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="catalog-v2-metrics">
        <div className="bg-white rounded-xl border p-4 text-center">
          <Package className="w-6 h-6 mx-auto text-blue-500 mb-1" />
          <div className="text-2xl font-bold text-gray-900">{items.length}</div>
          <div className="text-xs text-gray-500">Items Total</div>
        </div>
        <div className="bg-white rounded-xl border p-4 text-center">
          <CheckCircle2 className="w-6 h-6 mx-auto text-green-500 mb-1" />
          <div className="text-2xl font-bold text-green-600">{syncedCount}</div>
          <div className="text-xs text-gray-500">Sincronizados</div>
        </div>
        <div className="bg-white rounded-xl border p-4 text-center">
          <Image className="w-6 h-6 mx-auto text-purple-500 mb-1" />
          <div className="text-2xl font-bold text-purple-600">{withImage}</div>
          <div className="text-xs text-gray-500">Com Imagem</div>
        </div>
        <div className="bg-white rounded-xl border p-4 text-center">
          <XCircle className="w-6 h-6 mx-auto text-red-500 mb-1" />
          <div className="text-2xl font-bold text-red-600">{failedCount}</div>
          <div className="text-xs text-gray-500">Com Falha</div>
        </div>
      </div>

      {/* Checklist de Homologacao */}
      {checklist?.checks && (
        <div className="bg-white rounded-xl border p-5" data-testid="catalog-v2-checklist">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-red-500" />
            Checklist de Homologacao iFood
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <StatusBadge ok={checklist.checks.get_catalogs?.ok} label="GET Catalogs" />
            <StatusBadge ok={checklist.checks.get_categories?.ok} label={`GET Categories (${checklist.checks.get_categories?.count || 0})`} />
            <StatusBadge ok={checklist.checks.items_synced?.ok} label={`Items Synced (${checklist.checks.items_synced?.synced}/${checklist.checks.items_synced?.total})`} />
            <StatusBadge ok={checklist.checks.descriptions_valid?.ok} label={`Descricoes (${checklist.checks.descriptions_valid?.invalid_count || 0} invalidas)`} />
            <StatusBadge ok={checklist.checks.images_uploaded?.ok} label={`Imagens (${checklist.checks.images_uploaded?.with_image}/${checklist.checks.images_uploaded?.total})`} />
            <StatusBadge ok={checklist.checks.complements?.ok} label={`Complementos (${checklist.checks.complements?.groups} grupos, ${checklist.checks.complements?.options} opcoes)`} />
          </div>
        </div>
      )}

      {/* Acoes */}
      <div className="bg-white rounded-xl border p-5" data-testid="catalog-v2-actions">
        <h3 className="font-bold text-gray-900 mb-4">Acoes</h3>
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={handleUploadAll}
            disabled={uploading}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="btn-upload-all-images"
          >
            {uploading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? 'Enviando...' : 'Upload Todas Imagens'}
          </Button>
          <Button
            variant="outline"
            onClick={handleValidate}
            data-testid="btn-validate-all"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Validar Items
          </Button>
          <Button
            variant="outline"
            onClick={fetchCategories}
            data-testid="btn-load-categories"
          >
            <Eye className="w-4 h-4 mr-2" />
            Ver Categorias iFood
          </Button>
        </div>
        {uploadProgress && (
          <div className="mt-3 p-3 bg-purple-50 rounded-lg text-sm text-purple-700" data-testid="upload-progress">
            <RefreshCw className="w-4 h-4 inline animate-spin mr-2" />
            {uploadProgress}
          </div>
        )}
      </div>

      {/* Categorias iFood (quando carregadas) */}
      {categories && (
        <div className="bg-white rounded-xl border p-5" data-testid="catalog-v2-categories">
          <h3 className="font-bold text-gray-900 mb-4">
            Categorias no iFood ({categories.total_categories})
          </h3>
          <div className="space-y-2">
            {categories.categories?.map((cat) => (
              <div key={cat.id} className="border rounded-lg">
                <button
                  onClick={() => setExpandedCat(expandedCat === cat.id ? null : cat.id)}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50"
                  data-testid={`cat-${cat.id}`}
                >
                  <div className="flex items-center gap-2">
                    {expandedCat === cat.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <span className="font-medium">{cat.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      cat.status === 'AVAILABLE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>{cat.status}</span>
                  </div>
                  <span className="text-sm text-gray-500">{cat.items_count} items</span>
                </button>
                {expandedCat === cat.id && (
                  <div className="border-t px-3 py-2 bg-gray-50 text-xs text-gray-600">
                    ID: {cat.id}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista de Items */}
      <div className="bg-white rounded-xl border" data-testid="catalog-v2-items-list">
        <div className="p-4 border-b flex items-center gap-3">
          <h3 className="font-bold text-gray-900">Items no Catalogo ({filteredItems.length})</h3>
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar produto..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-200"
              data-testid="search-items-v2"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="items-table-v2">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Produto</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Descricao</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-600">Preco</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600">Status</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600">Sync</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600">Imagem</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600">Acao</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredItems.map((item) => (
                <tr key={item.product_id} className="hover:bg-gray-50" data-testid={`item-row-${item.product_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{item.name}</div>
                    <div className="text-xs text-gray-400 font-mono">{item.external_code?.substring(0, 20)}...</div>
                  </td>
                  <td className="px-4 py-3 max-w-xs">
                    <div className="text-gray-600 text-xs truncate" title={item.description}>
                      {item.description || <span className="text-red-500 font-semibold">SEM DESCRICAO</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-900">
                    R$ {item.price?.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                      item.status_ifood === 'AVAILABLE'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>{item.status_ifood || '-'}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                      item.sync_status === 'SYNCED' || item.sync_status === 'SYNCED_EXISTING'
                        ? 'bg-green-100 text-green-700'
                        : item.sync_status === 'FAILED'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-yellow-100 text-yellow-700'
                    }`}>{item.sync_status}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {item.image_path
                      ? <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />
                      : <XCircle className="w-5 h-5 text-gray-300 mx-auto" />}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {!item.image_path && item.is_active_local && (
                      <button
                        onClick={() => handleUploadSingle(item.product_id)}
                        className="text-xs text-purple-600 hover:text-purple-800 font-semibold"
                        data-testid={`btn-upload-${item.product_id}`}
                      >
                        <Upload className="w-3.5 h-3.5 inline mr-1" />
                        Upload
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default IFoodCatalogV2Tab;
